import { PropsWithChildren, useEffect, useState } from 'react'

import { Attendee, Meeting } from '../../../../common/src/api/www'

import { Information, MeetingContext } from './MeetingContext'

const STORAGE_KEY = 'meeting-state'

type StoredState = {
  meeting?: Meeting | null
  attendee?: Attendee | null
  information?: Information | null
  isRedial: boolean
}

// Initialize state from session storage
function getInitialState() {
  const storedState = sessionStorage.getItem(STORAGE_KEY)
  if (storedState) {
    try {
      const parsed = JSON.parse(storedState) as StoredState
      return {
        meeting: parsed.meeting ?? null,
        attendee: parsed.attendee ?? null,
        information: parsed.information ?? null,
        isRedial: parsed.isRedial ?? false,
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse stored state:', e)
    }
  }
  return {
    meeting: undefined,
    attendee: undefined,
    information: undefined,
    isRedial: false,
  }
}

export function MeetingProvider({ children }: PropsWithChildren) {
  const initialState = getInitialState()
  const [meeting, setMeeting] = useState<Meeting | null | undefined>(
    initialState.meeting
  )
  const [attendee, setAttendee] = useState<Attendee | null | undefined>(
    initialState.attendee
  )
  const [information, setInformation] = useState<
    Information | null | undefined
  >(initialState.information)
  const [isRedial, setIsRedial] = useState(initialState.isRedial)

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state: StoredState = {
      meeting,
      attendee,
      information,
      isRedial,
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [meeting, attendee, information, isRedial])

  return (
    <MeetingContext.Provider
      value={{
        meeting,
        setMeeting,
        attendee,
        setAttendee,
        information,
        setInformation,
        isRedial,
        setIsRedial,
      }}
    >
      {children}
    </MeetingContext.Provider>
  )
}
